/**
 * User TMA API Client
 * Handles communication with backend User API for Master/Client profiles
 */

const API_BASE_URL = '/api/user';

/**
 * Get Telegram initData from WebApp for authentication
 */
function getTelegramInitData(): string | undefined {
  const tg = window.Telegram?.WebApp;
  const initData = tg?.initData;
  return typeof initData === 'string' && initData.length > 0 ? initData : undefined;
}

function getDevTelegramId(): string | undefined {
  if (!import.meta.env.DEV) {
    return undefined;
  }
  const devId = import.meta.env.VITE_DEV_TELEGRAM_ID;
  return typeof devId === 'string' && devId.length > 0 ? devId : undefined;
}

/**
 * Base fetch wrapper with authentication
 */
async function userApiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const initData = getTelegramInitData();
  const devTelegramId = !initData ? getDevTelegramId() : undefined;
  if (!initData && !devTelegramId) {
    throw new Error('Missing Telegram initData');
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
      ...(devTelegramId ? { 'X-Dev-Telegram-Id': devTelegramId } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Types
export type UserRole = 'CLIENT' | 'MASTER' | 'DISPATCHER';

export interface Equipment {
  id: string;
  brand: string;
  model: string;
  address: string;
  latitude?: number;
  longitude?: number;
  lastService?: string;
  createdAt: string;
}

export interface MasterProfile {
  id: string;
  telegramId: string;
  name: string;
  phone: string | null;
  role: 'MASTER';
  photoUrl?: string;
  masterStatus: string;
  masterType: string | null;
  rating: number | null;
  reviewCount: number;
  balance: string;
  commissionDebt: string;
  districts: string[];
  brands: string[];
  createdAt: string;
}

export interface ClientProfile {
  id: string;
  telegramId: string;
  name: string;
  phone: string | null;
  role: 'CLIENT';
  photoUrl?: string;
  equipment: Equipment[];
  createdAt: string;
}

export interface DispatcherProfile {
  id: string;
  telegramId: string;
  name: string;
  phone: string | null;
  role: 'DISPATCHER';
  photoUrl?: string;
  createdAt: string;
}

export type UserProfile = MasterProfile | ClientProfile | DispatcherProfile;

export interface PublicMasterProfile {
  id: string;
  name: string;
  masterType: string | null;
  rating: number | null;
  reviewCount: number;
  masterStatus: string;
  verified: boolean;
  districts: string[];
  brands: string[];
  createdAt: string;
}

export interface UserOrder {
  id: string;
  orderNumber: number;
  type: 'VIDEO_CONSULT' | 'REPAIR' | 'MAINTENANCE';
  status: string;
  description: string;
  estimatedPrice: string | null;
  finalPrice: string | null;
  paymentMethod: string | null;
  scheduledAt: string | null;
  client?: {
    id: string;
    name: string;
    phone: string | null;
  };
  master: {
    id: string;
    name: string;
    rating: number | null;
  } | null;
  createdAt: string;
  completedAt: string | null;
}

export interface Review {
  id: string;
  orderId: string;
  rating: number;
  comment: string | null;
  clientName: string;
  createdAt: string;
}

export interface ServiceHistoryItem {
  id: string;
  orderNumber: number;
  type: 'VIDEO_CONSULT' | 'REPAIR' | 'MAINTENANCE';
  description: string;
  finalPrice: string | null;
  photos: string[];
  equipment: {
    brand: string;
    model: string;
    address: string;
  } | null;
  master: {
    id: string;
    name: string;
    rating: number | null;
  } | null;
  completedAt: string | null;
}

export type ClientDocType =
  | 'CLIENT_EQUIPMENT_PASSPORT'
  | 'CLIENT_VDGO_CONTRACT'
  | 'CLIENT_SERVICE_ACT';

export interface ClientDocument {
  id: string;
  type: ClientDocType;
  originalName: string;
  verified: boolean;
  verifiedAt: string | null;
  createdAt: string;
}

export interface BalanceHistoryItem {
  id: string;
  action: string;
  details: Record<string, unknown>;
  createdAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// API Functions

/**
 * Fetch current user profile
 */
export async function fetchUserProfile(): Promise<UserProfile> {
  return userApiFetch<UserProfile>('/profile');
}

/**
 * Fetch public master profile (for QR verification)
 */
export async function fetchPublicMasterProfile(masterId: string): Promise<PublicMasterProfile> {
  return userApiFetch<PublicMasterProfile>(`/master/${masterId}`);
}

/**
 * Fetch client's equipment
 */
export async function fetchEquipment(): Promise<{ data: Equipment[] }> {
  return userApiFetch<{ data: Equipment[] }>('/equipment');
}

/**
 * Fetch user's orders
 */
export async function fetchUserOrders(params: {
  status?: string;
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<UserOrder>> {
  const searchParams = new URLSearchParams();

  if (params.status) {
    searchParams.set('status', params.status);
  }
  if (params.page) {
    searchParams.set('page', params.page.toString());
  }
  if (params.limit) {
    searchParams.set('limit', params.limit.toString());
  }

  const query = searchParams.toString();
  return userApiFetch<PaginatedResponse<UserOrder>>(`/orders${query ? `?${query}` : ''}`);
}

/**
 * Fetch master's reviews
 */
export async function fetchReviews(params: {
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<Review>> {
  const searchParams = new URLSearchParams();

  if (params.page) {
    searchParams.set('page', params.page.toString());
  }
  if (params.limit) {
    searchParams.set('limit', params.limit.toString());
  }

  const query = searchParams.toString();
  return userApiFetch<PaginatedResponse<Review>>(`/reviews${query ? `?${query}` : ''}`);
}

/**
 * Fetch client's service history
 */
export async function fetchServiceHistory(): Promise<{ data: ServiceHistoryItem[] }> {
  return userApiFetch<{ data: ServiceHistoryItem[] }>('/service-history');
}

/**
 * Fetch master's balance history
 */
export async function fetchBalanceHistory(): Promise<{ data: BalanceHistoryItem[] }> {
  return userApiFetch<{ data: BalanceHistoryItem[] }>('/balance-history');
}

/**
 * Toggle master's online/offline status
 */
export async function updateMasterStatus(status: 'ONLINE' | 'OFFLINE'): Promise<{ success: boolean; status: string }> {
  return userApiFetch<{ success: boolean; status: string }>('/status', {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

/**
 * Fetch available orders for master (BIDDING status)
 */
export async function fetchAvailableOrders(params?: {
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<UserOrder>> {
  const searchParams = new URLSearchParams();
  searchParams.set('status', 'BIDDING');

  if (params?.page) {
    searchParams.set('page', params.page.toString());
  }
  if (params?.limit) {
    searchParams.set('limit', params.limit.toString());
  }

  return userApiFetch<PaginatedResponse<UserOrder>>(`/orders?${searchParams.toString()}`);
}

/**
 * Fetch client's documents
 */
export async function fetchDocuments(): Promise<{ data: ClientDocument[] }> {
  return userApiFetch<{ data: ClientDocument[] }>('/documents');
}

/**
 * Upload a new document
 */
export async function uploadDocument(params: {
  type: ClientDocType;
  fileData: string; // base64 encoded
  fileName: string;
  mimeType: string;
}): Promise<{ success: boolean; document: ClientDocument }> {
  return userApiFetch<{ success: boolean; document: ClientDocument }>('/documents', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * Download a document as blob (decrypted on backend)
 */
export async function downloadDocument(documentId: string): Promise<Blob> {
  const initData = getTelegramInitData();
  const devTelegramId = !initData ? getDevTelegramId() : undefined;
  if (!initData && !devTelegramId) {
    throw new Error('Missing Telegram initData');
  }

  const response = await fetch(`${API_BASE_URL}/documents/${documentId}/download`, {
    headers: {
      ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
      ...(devTelegramId ? { 'X-Dev-Telegram-Id': devTelegramId } : {}),
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Download failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.blob();
}

/**
 * Delete a document
 */
export async function deleteDocument(documentId: string): Promise<{ success: boolean }> {
  return userApiFetch<{ success: boolean }>(`/documents/${documentId}`, {
    method: 'DELETE',
  });
}

/**
 * Read file as base64
 */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
